import TurndownService from "turndown";
import { gfm } from "turndown-plugin-gfm";
import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * Drive stores every prompt as Markdown: it survives a round trip through any
 * text box, an LLM reads it natively, and it renders back into headings, bold
 * and lists here.
 *
 * Pasting from Google Docs or Word hands the browser HTML — this turns that
 * HTML into Markdown instead of letting the textarea flatten it to one run of
 * text, which is what lost every heading and bullet before.
 */

const turndown = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  emDelimiter: "_",
  strongDelimiter: "**",
  hr: "---",
});
turndown.use(gfm);

// No escaping. Turndown backslash-escapes anything that COULD read as Markdown,
// so "[BOOK TITLE]" became "\[BOOK TITLE\]" and "1. ABOUT" became "1\. ABOUT"
// — and Copy handed those backslashes to the LLM. A prompt's placeholders and
// numbering must survive copy exactly; at worst a line like "2. BOOK FIDELITY"
// renders here as a numbered item, which reads the same.
turndown.escape = (text) => text;

// "- item", not turndown's default "-   item": the same Markdown, but readable
// as text too, which is how an LLM prompt gets pasted.
turndown.addRule("listItem", {
  filter: "li",
  replacement: (content, node) => {
    const parent = node.parentNode;
    let prefix = "- ";
    if (parent?.nodeName === "OL") {
      const start = Number(parent.getAttribute("start")) || 1;
      prefix = `${start + Array.prototype.indexOf.call(parent.children, node)}. `;
    }
    const body = content.replace(/^\n+/, "").replace(/\n+$/, "\n").replace(/\n/gm, "\n   ");
    return prefix + body + (node.nextSibling && !/\n$/.test(body) ? "\n" : "");
  },
});

const isBold = (style) => /font-weight:\s*(bold|[6-9]00)/i.test(style);
const isItalic = (style) => /font-style:\s*italic/i.test(style);

/**
 * Google Docs and Word mark formatting with inline styles rather than tags,
 * and Docs wraps the whole selection in a `<b style="font-weight:normal">` —
 * taken literally, every paste would come out entirely bold, or not bold at
 * all. Rewrite those into plain tags first.
 */
const normalise = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Docs' outer wrapper: a <b> that is explicitly NOT bold.
  doc.querySelectorAll('b[id^="docs-internal-guid"], b[style*="font-weight:normal"]').forEach((el) => {
    el.replaceWith(...el.childNodes);
  });

  // Word's empty <o:p> tags, and the bullet glyph it draws for each list item
  // (a "·" in a span it marks mso-list:Ignore).
  doc.querySelectorAll("o\\:p").forEach((el) => el.remove());
  doc.querySelectorAll('[style*="mso-list:Ignore"], [style*="mso-list: Ignore"]').forEach((el) => el.remove());

  // Word writes a list as paragraphs of class MsoListParagraph*, not <ul>.
  // Gather each run of them into a real list.
  let list = null;
  doc.querySelectorAll("p").forEach((p) => {
    if (!/^MsoListParagraph/.test(p.className || "")) {
      list = null;
      return;
    }
    if (!list || list.nextElementSibling !== p) {
      list = doc.createElement("ul");
      p.before(list);
    }
    const li = doc.createElement("li");
    li.append(...p.childNodes);
    p.remove();
    list.append(li);
  });

  doc.querySelectorAll("span[style]").forEach((span) => {
    // A heading is already bold; wrapping it again only adds noise.
    if (span.closest("h1,h2,h3,h4,h5,h6")) return;
    const style = span.getAttribute("style") || "";
    const node = span;
    if (isBold(style)) {
      const strong = doc.createElement("strong");
      strong.append(...node.childNodes);
      node.append(strong);
    }
    if (isItalic(style)) {
      const em = doc.createElement("em");
      em.append(...node.childNodes);
      node.append(em);
    }
  });

  // Docs puts each list item's text in a <p>, which turndown would space out.
  doc.querySelectorAll("li > p").forEach((p) => p.replaceWith(...p.childNodes));

  return doc.body.innerHTML;
};

/** Clipboard HTML (Docs, Word, a web page) → Markdown. */
export const htmlToMarkdown = (html) =>
  turndown
    .turndown(normalise(html))
    // Word leaves non-breaking spaces and runs of blank lines behind.
    .replace(/\u00a0/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * Markdown → sanitised HTML for display. `breaks` keeps a single newline as a
 * line break, so prompts written as plain text before Markdown arrived still
 * read the way they were typed.
 */
export const markdownToHtml = (markdown) =>
  DOMPurify.sanitize(marked.parse(markdown || "", { gfm: true, breaks: true, async: false }));

/**
 * Copies the prompt as rich text AND as Markdown in one go: Google Docs and
 * Word take the formatted version, a plain text box (ChatGPT, a code editor)
 * takes the Markdown.
 */
export const copyFormatted = async (markdown) => {
  if (!window.ClipboardItem || !navigator.clipboard?.write || !window.isSecureContext) {
    throw new Error("Rich copy unavailable");
  }
  const html = `<meta charset="utf-8">${markdownToHtml(markdown)}`;
  await navigator.clipboard.write([
    new window.ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([markdown], { type: "text/plain" }),
    }),
  ]);
};
